import { createFileRoute, Link } from "@tanstack/react-router";
import { buildHead } from "@/lib/seo";
import { AGENCY } from "@/lib/agency";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const UPDATED = "June 25, 2026";

export const Route = createFileRoute("/privacy")({
  head: () =>
    buildHead({
      title: `Privacy Policy - ${AGENCY.name}`,
      description: `How ${AGENCY.name} collects, uses, and shares the information you provide when you request business financing.`,
      path: "/privacy",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Privacy Policy", path: "/privacy" },
      ],
    }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated={UPDATED}
      intro={`This policy explains what information ${AGENCY.name} collects when you use this website or request financing, how we use it, and who we share it with.`}
    >
      <LegalSection heading="Information we collect">
        <p>
          When you submit a form, request a quote, or contact us, we collect
          the information you choose to give us: typically your name, business
          name, phone number, email address, and details about your business
          and financing needs. We may also collect basic technical information (such as
          device and usage data) automatically through standard web technologies.
        </p>
      </LegalSection>

      <LegalSection heading="How we use your information">
        <p>We use the information you provide to:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>respond to your inquiry and contact you about financing;</li>
          <li>
            match your request with lenders and funding partners who may be able
            to make an offer;
          </li>
          <li>operate, improve, and secure this website; and</li>
          <li>comply with legal and regulatory obligations.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="How we share your information">
        <p>
          Because {AGENCY.name} is an advisory that places financing with
          third-party lenders, we share the information you submit with lending
          and funding partners so they can evaluate your request and present
          options. We also use third-party service providers, including a form
          and customer-relationship-management (CRM) provider that powers our
          application forms, to operate this site. Those providers process your
          information on our behalf and under their own privacy terms.
        </p>
        <p>
          We may also disclose information when required by law, to protect our
          rights, or in connection with a business transfer.
        </p>
      </LegalSection>

      <LegalSection heading="Your choices">
        <p>
          You can ask us to access, correct, or delete the information you have
          provided, and you can opt out of marketing communications at any time,
          by contacting us using the details below. Some information may be
          retained as needed to provide the service or meet legal obligations.
        </p>
      </LegalSection>

      <LegalSection heading="Data security and retention">
        <p>
          We take reasonable measures to protect the information you provide, but
          no method of transmission or storage is completely secure, and we
          cannot guarantee absolute security. We keep information only as long as
          needed for the purposes described here or as required by law.
        </p>
      </LegalSection>

      <LegalSection heading="Children's privacy">
        <p>
          This website and our services are intended for business owners and are
          not directed to children under 18. We do not knowingly collect
          information from children.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes
          will be reflected by updating the effective date above.
        </p>
      </LegalSection>

      <LegalSection heading="Contact us">
        <p>
          Questions about your privacy? Call{" "}
          <a href={AGENCY.phoneHref} className="font-medium text-foreground hover:text-accent">
            {AGENCY.phone}
          </a>{" "}
          or use our{" "}
          <Link to="/contact" className="font-medium text-foreground hover:text-accent">
            contact page
          </Link>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
